import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import {
  type FinanceAssignee,
  type FinanceRequestDetail,
} from '@/services/financeService'
import { formatFinanceStatus } from '@/hooks/useFinanceiroPage'
import {
  buildC4StatusSelectOptions,
  formatFinanceRequestStatus,
  getFinanceRequestStatusesForType,
  isC4ContractRequestType,
  normalizeC4ContractStatus,
} from '@/components/Financeiro/financeC4ContractStatus'
import { FinanceSlaBadge } from '@/components/Financeiro/FinanceSlaBadge'
import { FINANCE_DETAIL_FIELD_CLASS } from '@/components/Financeiro/FinanceDetailSectionCard'

function sortJaniceFirst(assignees: FinanceAssignee[]) {
  const janice = assignees.find((a) => a.name.toLowerCase().includes('janice'))
  if (!janice) return assignees
  return [janice, ...assignees.filter((a) => a.id !== janice.id)]
}

type Props = {
  detail: FinanceRequestDetail
  canManageQueue: boolean
  assignees: FinanceAssignee[]
  onUpdateStatus: (status: string, approvalPendingFrom?: string) => Promise<boolean>
  onAssign: (assigneeId: string | null) => Promise<boolean>
}

export function FinanceRequestQueuePanel({
  detail,
  canManageQueue,
  assignees,
  onUpdateStatus,
  onAssign,
}: Props) {
  const [pendingStatus, setPendingStatus] = useState('')
  const [approvalFrom, setApprovalFrom] = useState('')
  const sortedAssignees = sortJaniceFirst(assignees)
  const isC4Contract = isC4ContractRequestType(detail.type)
  const statusOptions = isC4Contract
    ? buildC4StatusSelectOptions(detail.status)
    : getFinanceRequestStatusesForType(detail.type).includes(detail.status)
      ? getFinanceRequestStatusesForType(detail.type)
      : [...getFinanceRequestStatusesForType(detail.type), detail.status]
  const selectedStatus = isC4Contract ? normalizeC4ContractStatus(detail.status) : detail.status
  const formatStatus = (status: string) =>
    isC4Contract ? formatFinanceRequestStatus(status, detail.type) : formatFinanceStatus(status)
  const emptyAssigneeLabel = isC4Contract ? 'Janice (padrão)' : 'Sem responsável'

  const handleStatusChange = async (status: string) => {
    if (!isC4Contract && status === 'AGUARDANDO_APROVACAO') {
      setPendingStatus(status)
      setApprovalFrom(detail.approvalPendingFrom ?? '')
      return
    }
    await onUpdateStatus(status)
  }

  const confirmApproval = async () => {
    if (!pendingStatus) return
    const ok = await onUpdateStatus(pendingStatus, approvalFrom.trim() || undefined)
    if (ok) setPendingStatus('')
  }

  return (
    <div className='space-y-4'>
      <div className='space-y-1'>
        <label className='text-[11px] font-medium uppercase tracking-wider text-text-muted'>
          Status
        </label>
        {canManageQueue ? (
          <select
            className={FINANCE_DETAIL_FIELD_CLASS}
            value={selectedStatus}
            onChange={(e) => void handleStatusChange(e.target.value)}
          >
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {formatStatus(s)}
              </option>
            ))}
          </select>
        ) : (
          <p className='text-sm font-semibold text-text-primary'>{formatStatus(selectedStatus)}</p>
        )}
      </div>

      {(canManageQueue || detail.assignee) && (
        <div className='space-y-1'>
          <label className='text-[11px] font-medium uppercase tracking-wider text-text-muted'>
            Responsável
          </label>
          {canManageQueue ? (
            <select
              className={FINANCE_DETAIL_FIELD_CLASS}
              value={detail.assignee?.id ?? ''}
              onChange={(e) => void onAssign(e.target.value ? e.target.value : null)}
            >
              <option value=''>{emptyAssigneeLabel}</option>
              {sortedAssignees.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {a.name.toLowerCase().includes('janice') ? ' (padrão)' : ''}
                </option>
              ))}
            </select>
          ) : (
            <p className='text-sm font-medium text-text-primary'>{detail.assignee?.name ?? '—'}</p>
          )}
        </div>
      )}

      <div className='flex items-center justify-between gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5'>
        <span className='text-xs font-medium uppercase tracking-wider text-text-muted'>SLA</span>
        <FinanceSlaBadge slaDueAt={detail.slaDueAt} status={detail.status} />
      </div>

      {detail.slaDueAt && (
        <p className='text-xs text-text-muted'>
          Prazo: {new Date(detail.slaDueAt).toLocaleString('pt-BR')}
        </p>
      )}

      <div className='space-y-1 border-t border-white/[0.06] pt-3'>
        <p className='text-[11px] font-medium uppercase tracking-wider text-text-muted'>Solicitante</p>
        <p className='text-sm font-medium text-text-primary'>{detail.requester?.name ?? '—'}</p>
        {detail.requester?.email && (
          <p className='text-xs text-text-muted'>{detail.requester.email}</p>
        )}
      </div>

      {!isC4Contract && detail.status === 'AGUARDANDO_APROVACAO' && detail.approvalPendingFrom && (
        <div className='rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2 text-sm'>
          <p className='text-xs font-medium uppercase tracking-wider text-amber-600/90 dark:text-amber-400'>
            Aprovação pendente
          </p>
          <p className='mt-1 font-medium text-text-primary'>{detail.approvalPendingFrom}</p>
        </div>
      )}

      {!isC4Contract && pendingStatus === 'AGUARDANDO_APROVACAO' && (
        <div className='rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm'>
          <p className='mb-2 font-medium text-text-primary'>Quem deve aprovar?</p>
          <input
            className={`${FINANCE_DETAIL_FIELD_CLASS} mb-2`}
            placeholder='Nome ou e-mail do gestor'
            value={approvalFrom}
            onChange={(e) => setApprovalFrom(e.target.value)}
          />
          <div className='flex gap-2'>
            <Button type='button' size='sm' onClick={() => void confirmApproval()}>
              Confirmar
            </Button>
            <Button type='button' size='sm' variant='secondary' onClick={() => setPendingStatus('')}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
